<template>
  <div class="policy-page">
    <!-- Banner -->
    <div class="page-banner">
      <div class="banner-inner">
        <h1>{{ $t('footer.servicePolicy') }}</h1>
        <p>Service Policy &amp; Terms</p>
      </div>
    </div>

    <div class="page-content">
      <!-- 目录 -->
      <div class="policy-layout">
        <aside class="toc">
          <h4>目录</h4>
          <a v-for="item in sections" :key="item.id"
             href="javascript:void(0)"
             :class="{ active: activeId === item.id }"
             @click="scrollTo(item.id)">{{ item.title }}</a>
        </aside>

        <article class="policy-body">
          <p class="update-date">最后更新：2026 年 3 月 1 日 &nbsp;|&nbsp; Last Updated: March 1, 2026</p>

          <section id="overview">
            <h2>1. 总则 / Overview</h2>
            <p>欢迎使用网班汉语水平快速测评中心（NBCFA）。本服务政策适用于所有使用 NBCFA 平台（以下简称"本平台"）的用户，包括参加测评的学生、机构用户及其他访问者。使用本平台即表示您已阅读、理解并同意遵守本政策的全部条款。</p>
            <p class="en">Welcome to the Net Ban Chinese Fast Assessment Center (NBCFA). This Service Policy applies to all users of the NBCFA platform, including students, institutional users, and visitors. By using the platform, you acknowledge that you have read, understood, and agreed to all terms of this policy.</p>
          </section>

          <section id="service">
            <h2>2. 服务说明 / Service Description</h2>
            <p>NBCFA 提供基于人工智能的汉语水平在线测评服务，主要包括：</p>
            <ul>
              <li>语音测评（Speaking Assessment）：通过录音分析评估发音、流利度和语调</li>
              <li>阅读测评（Reading Assessment）：评估阅读理解与语音作答能力</li>
              <li>书写测评（Writing Assessment）：评估汉字书写与书面表达能力</li>
            </ul>
            <p>测评结果以 CEFR 等级（A1 / A2 / B1 / B2 / C1 / C2）呈现，并提供详细的分项成绩报告。</p>
            <p class="en">NBCFA provides AI-powered online Chinese proficiency assessments. Results are presented using CEFR levels (A1 – C2) with detailed sub-score reports.</p>
          </section>

          <section id="account">
            <h2>3. 账号与隐私 / Account &amp; Privacy</h2>
            <p>3.1 用户须使用真实信息完成注册，不得冒用他人身份参加测评。</p>
            <p>3.2 本平台通过 HTTPS 加密传输所有数据，测评录音及文字答案仅用于评分目的，不会对外披露。</p>
            <p>3.3 用户可随时在"个人中心"中修改个人资料或申请注销账号。</p>
            <p class="en">3.1 Users must register with accurate information. Impersonation is strictly prohibited. 3.2 All data is transmitted via HTTPS. Audio recordings and text answers are used solely for scoring purposes and will not be disclosed. 3.3 Users may update personal data or request account deletion at any time in the User Center.</p>
          </section>

          <section id="assessment-rules">
            <h2>4. 测评规则 / Assessment Rules</h2>
            <ul>
              <li>每次测评共 13 题（语音 5 题 + 阅读 5 题 + 书写 3 题），全程约 30 分钟</li>
              <li>测评开始后不支持中途退出；如强制退出，本次成绩不计入记录</li>
              <li>同一账号每日最多进行 3 次测评</li>
              <li>严禁使用任何辅助工具或作弊行为；一经发现，成绩将被取消</li>
              <li>测评需要麦克风权限；请确保设备正常工作后再开始</li>
            </ul>
            <p class="en">Each assessment contains 13 questions (~30 min). Abandoning mid-assessment will result in no score being recorded. A maximum of 3 assessments per account per day is allowed. Cheating results in score invalidation.</p>
          </section>

          <section id="scores">
            <h2>5. 成绩与报告 / Scores &amp; Reports</h2>
            <p>5.1 各阶段分数为该阶段所有题目的平均分（满分 100 分），总分亦为所有题目的综合平均分。</p>
            <p>5.2 等级对应关系如下：</p>
            <table class="grade-table">
              <thead><tr><th>等级 / Level</th><th>平均分区间 / Avg Score Range</th><th>描述 / Description</th></tr></thead>
              <tbody>
                <tr><td><span class="grade-pill c2">C2</span></td><td>80 – 100</td><td>精通 / Mastery</td></tr>
                <tr><td><span class="grade-pill c1">C1</span></td><td>70 – 79</td><td>高级 / Advanced</td></tr>
                <tr><td><span class="grade-pill b2">B2</span></td><td>60 – 69</td><td>中高级 / Upper-Intermediate</td></tr>
                <tr><td><span class="grade-pill b1">B1</span></td><td>50 – 59</td><td>中级 / Intermediate</td></tr>
                <tr><td><span class="grade-pill a2">A2</span></td><td>40 – 49</td><td>初级 / Elementary</td></tr>
                <tr><td><span class="grade-pill a1">A1</span></td><td>0 – 39</td><td>入门 / Beginner</td></tr>
              </tbody>
            </table>
            <p>5.3 成绩报告将在测评完成后立即生成，可在"个人中心 → 我的测试"中查看和下载。</p>
          </section>

          <section id="refund">
            <h2>6. 付费与退款 / Payment &amp; Refunds</h2>
            <p>6.1 目前 NBCFA 提供免费测评服务；如未来推出付费增值服务，将提前公告并更新本政策。</p>
            <p>6.2 如发生错误扣费，请在 7 个工作日内联系客服，核实后将全额退款至原支付渠道。</p>
            <p class="en">6.1 NBCFA currently provides free assessments. Any future paid services will be announced in advance. 6.2 Erroneous charges will be refunded within 7 business days upon verification.</p>
          </section>

          <section id="disclaimer">
            <h2>7. 免责声明 / Disclaimer</h2>
            <p>7.1 NBCFA 成绩报告仅供参考，不作为任何官方语言资质认证的替代。</p>
            <p>7.2 本平台对因不可抗力（如网络中断、服务器故障）导致的测评中断不承担责任，但将尽力提供补测机会。</p>
            <p>7.3 本政策的最终解释权归 NETCLASS（纳斯达克：NTCL）所有。</p>
            <p class="en">7.1 NBCFA scores are for reference only and do not replace official language certifications. 7.2 NETCLASS is not liable for interruptions caused by force majeure but will endeavor to provide make-up assessment opportunities. 7.3 Final interpretation of this policy belongs to NETCLASS (Nasdaq: NTCL).</p>
          </section>

          <section id="contact-info">
            <h2>8. 联系我们 / Contact</h2>
            <p>如对本政策有任何疑问，请通过以下方式联系我们：</p>
            <p>📧 <a href="mailto:support@nbcfa.com">support@nbcfa.com</a></p>
            <p>📞 400-888-6223（工作日 9:00–18:00）</p>
            <p class="en">For questions about this policy, email us at <a href="mailto:support@nbcfa.com">support@nbcfa.com</a> or call 400-888-6223.</p>
          </section>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PolicyPage',
  data() {
    return {
      activeId: 'overview',
      sections: [
        { id: 'overview', title: '1. 总则' },
        { id: 'service', title: '2. 服务说明' },
        { id: 'account', title: '3. 账号与隐私' },
        { id: 'assessment-rules', title: '4. 测评规则' },
        { id: 'scores', title: '5. 成绩与报告' },
        { id: 'refund', title: '6. 付费与退款' },
        { id: 'disclaimer', title: '7. 免责声明' },
        { id: 'contact-info', title: '8. 联系我们' }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    scrollTo(id) {
      var el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      this.activeId = id
    },
    onScroll() {
      var offsets = this.sections.map(s => {
        var el = document.getElementById(s.id)
        return el ? { id: s.id, top: el.getBoundingClientRect().top } : null
      }).filter(Boolean)
      var visible = offsets.filter(o => o.top <= 120)
      if (visible.length > 0) {
        this.activeId = visible[visible.length - 1].id
      }
    }
  }
}
</script>

<style scoped>
.policy-page { background: #f0f2f5; min-height: 100vh; }

/* Banner */
.page-banner {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%);
  padding: 60px 0 50px;
}
.banner-inner { max-width: 1100px; margin: 0 auto; padding: 0 40px; }
.banner-inner h1 { color: #fff; font-size: 32px; font-weight: 800; margin: 0 0 8px; }
.banner-inner p { color: #94a3b8; font-size: 14px; margin: 0; }

/* Content */
.page-content { max-width: 1100px; margin: 0 auto; padding: 40px 40px 60px; }

/* Layout */
.policy-layout { display: flex; gap: 32px; align-items: flex-start; }

/* TOC Sidebar */
.toc {
  width: 200px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 12px;
  padding: 20px 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  position: sticky;
  top: 80px;
}
.toc h4 { font-size: 13px; font-weight: 700; color: #333; margin: 0 0 12px; text-transform: uppercase; letter-spacing: 0.5px; }
.toc a {
  display: block;
  font-size: 13px;
  color: #666;
  text-decoration: none;
  padding: 6px 8px;
  border-radius: 6px;
  margin-bottom: 2px;
  transition: all 0.2s;
  border-left: 3px solid transparent;
}
.toc a:hover { color: #2b7de9; background: #f5f8ff; }
.toc a.active { color: #2b7de9; background: #eff6ff; border-left-color: #2b7de9; font-weight: 600; }

/* Policy Body */
.policy-body {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 12px;
  padding: 40px 48px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.update-date { font-size: 12px; color: #999; margin: 0 0 32px; }

section { margin-bottom: 36px; scroll-margin-top: 90px; }
section h2 {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid #eff6ff;
}
section p {
  font-size: 14px;
  color: #444;
  line-height: 1.8;
  margin: 0 0 10px;
}
section p.en {
  font-size: 13px;
  color: #888;
  font-style: italic;
  border-left: 3px solid #dbeafe;
  padding-left: 12px;
  margin-top: 6px;
}
section ul {
  padding-left: 20px;
  margin: 0 0 10px;
}
section ul li {
  font-size: 14px;
  color: #444;
  line-height: 1.8;
  margin-bottom: 4px;
}
section a { color: #2b7de9; text-decoration: none; }
section a:hover { text-decoration: underline; }

/* Grade Table */
.grade-table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  font-size: 13px;
}
.grade-table th {
  background: #f7f8fc;
  padding: 10px 14px;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #e5e7eb;
}
.grade-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}
.grade-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 13px;
  color: #fff;
}
.grade-pill.c2 { background: #7c3aed; }
.grade-pill.c1 { background: #2563eb; }
.grade-pill.b2 { background: #059669; }
.grade-pill.b1 { background: #d97706; }
.grade-pill.a2 { background: #ea580c; }
.grade-pill.a1 { background: #dc2626; }

@media (max-width: 768px) {
  .policy-layout { flex-direction: column; }
  .toc { width: 100%; position: static; }
  .policy-body { padding: 24px 20px; }
  .page-content { padding: 24px 16px 40px; }
  .banner-inner { padding: 0 16px; }
}
</style>
